import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Users, AlertCircle } from 'lucide-react';
import { authService } from './services/authService';
import AuthLayout from './components/auth/AuthLayout';
import AuthCard from './components/auth/AuthCard';
import SignupLegalNotice from './components/auth/SignupLegalNotice';
import Button from './components/ui/Button';
import logger, { safeErrorEventDetails } from './utils/logger';
import { MIN_PASSWORD_LENGTH } from './config/constants';

function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      logger.errorEvent('invite_invalid_input', { field: 'token' });
      setError('Invalid invite link');
      return;
    }

    if (!fullName.trim()) {
      logger.errorEvent('invite_invalid_input', { field: 'name', reason: 'missing' });
      setError('Please enter your name');
      return;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
      logger.errorEvent('invite_invalid_input', { field: 'password', reason: 'min_length' });
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
      return;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password)) {
      logger.errorEvent('invite_invalid_input', { field: 'password', reason: 'missing_complexity' });
      setError('Password must contain uppercase, lowercase, and number');
      return;
    }
    if (password !== confirmPassword) {
      logger.errorEvent('invite_invalid_input', { field: 'confirmPassword', reason: 'mismatch' });
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      const data = await authService.acceptInvite(token, password, confirmPassword, fullName.trim());
      if (data.requires2FA) {
        navigate('/verify-2fa', { state: { email: data.email } });
      }
    } catch (err) {
      logger.errorEvent('invite_accept_failed', safeErrorEventDetails(err));
      setError(err.message || 'Invite acceptance failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <AuthCard>
          <div className="auth-heading mb-6">
            <div className="auth-status-icon">
              <Users className="h-10 w-10 text-[var(--color-text-primary)]" />
            </div>
            <h2 className="mb-2 text-2xl font-bold text-[var(--color-text-primary)]">Accept Invite</h2>
            <p className="text-[var(--color-text-secondary)]">Set your name and password to join the household.</p>
          </div>

          {error && (
            <div role="alert" className="auth-error mb-4 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="invite-full-name" className="mb-2 block text-sm font-semibold text-[var(--color-text-secondary)]">Your name</label>
              <input
                id="invite-full-name"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors duration-150"
                placeholder="First and last name"
                autoComplete="name"
                required
              />
            </div>
            <div>
              <label htmlFor="invite-password" className="mb-2 block text-sm font-semibold text-[var(--color-text-secondary)]">Password</label>
              <input
                id="invite-password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors duration-150"
                placeholder="••••••••"
                required
              />
            </div>
            <div>
              <label htmlFor="invite-confirm-password" className="mb-2 block text-sm font-semibold text-[var(--color-text-secondary)]">Confirm Password</label>
              <input
                id="invite-confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full rounded-xl border border-[var(--color-border-strong)] bg-[var(--color-surface-input)] px-4 py-3 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-muted)] transition-colors duration-150"
                placeholder="••••••••"
                required
              />
            </div>
            <Button
              type="submit"
              loading={loading}
              loadingLabel="Creating account…"
              fullWidth
            >
              Continue
            </Button>
            <SignupLegalNotice />
          </form>
      </AuthCard>
    </AuthLayout>
  );
}

export default AcceptInvite;
